'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import {
  Check,
  MessageSquareText,
  ScanSearch,
  ShieldCheck,
} from 'lucide-react';

import styles from './website-readiness-demo.module.css';

const titles = [
  'Check what Sarah knows',
  'Find the missing answer',
  'Let Sarah interview you',
  'Answer saved. Check again.',
];
const descriptions = [
  'Onboarding includes 20 questions based on the agent’s knowledge and role.',
  'Readiness shows where an answer needs more information.',
  'Sarah asks about the gap. Your team supplies the missing knowledge.',
  'The answer is saved and re-checked before your team decides to go live.',
];

export function WebsiteReadinessDemo({
  previewTime,
}: {
  previewTime?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState(previewTime ?? 0);
  useEffect(() => {
    if (previewTime !== undefined) return;
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let elapsed = 0;
    let previous = performance.now();
    let frame = 0;
    const observer = new IntersectionObserver(([entry]) => {
      visible = !!entry?.isIntersecting;
    });
    if (root.current) observer.observe(root.current);
    const tick = (now: number) => {
      if (media.matches) setTime(29000);
      else if (visible && !document.hidden) {
        elapsed = (elapsed + now - previous) % 34000;
        setTime(elapsed);
      }
      previous = now;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [previewTime]);
  const phase = time < 6000 ? 0 : time < 12000 ? 1 : time < 26000 ? 2 : 3;
  const answer =
    'Customers can request a payment plan by contacting our accounts team.';
  const typed = answer.slice(0, Math.max(0, Math.floor((time - 17000) / 55)));
  return (
    <div ref={root} className={styles.scene} data-readiness-phase={phase}>
      <div className={styles.panel}>
        <header className={styles.header}>
          <div>
            <ShieldCheck size={20} />
            <strong>Readiness check</strong>
          </div>
          <span>Agent onboarding</span>
        </header>
        <div className={styles.steps}>
          <span>
            <Check size={13} /> Create agent
          </span>
          <span>
            <Check size={13} /> Connect knowledge
          </span>
          <strong>Readiness</strong>
        </div>
        <div className={styles.body}>
          <div className={styles.identity}>
            <Image
              src="/images/agents/sarah-avatar.jpg"
              alt="Sarah"
              width={40}
              height={40}
              unoptimized
            />
            <div>
              <strong>Sarah</strong>
              <span>Before the first conversation</span>
            </div>
            <span className={styles.draft}>Draft</span>
          </div>
          {phase === 0 ? (
            <div className={styles.assessment}>
              <ScanSearch size={36} />
              <h3>Checking Sarah’s answers</h3>
              <p>Questions from her knowledge and role</p>
              <div className={styles.track}>
                <span style={{ width: `${Math.min(100, time / 60)}%` }} />
              </div>
              <strong>
                {Math.min(20, Math.floor(time / 300))} / 20 questions
              </strong>
              <div className={styles.checks}>
                <span>Knowledge</span>
                <span>Answer quality</span>
                <span>Action use</span>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.results}>
                <div>
                  <span>Readiness check</span>
                  <strong>20 questions reviewed</strong>
                </div>
                <span className={styles.status}>
                  {phase === 3 ? 'Re-checking answer' : 'Knowledge gap found'}
                </span>
              </div>
              <div className={styles.gap}>
                <span className={styles.gapLabel}>Knowledge gap</span>
                <strong>Payment plan policy</strong>
                <p>Can a customer pay their invoice in instalments?</p>
                {phase === 1 && (
                  <div className={styles.interviewButton}>
                    <MessageSquareText size={16} /> Let Sarah interview you
                  </div>
                )}
              </div>
              {phase >= 2 && (
                <div className={styles.interview}>
                  <header>
                    <MessageSquareText size={16} />
                    <strong>Sarah is interviewing you</strong>
                  </header>
                  <p className={styles.prompt}>
                    I’m missing your payment plan policy. How should I help
                    customers who ask to pay in instalments?
                  </p>
                  {(time >= 17000 || phase === 3) && (
                    <p className={styles.answer}>
                      {phase === 3 ? answer : typed}
                      <span aria-hidden="true">{time < 21500 ? '▎' : ''}</span>
                    </p>
                  )}
                  {phase === 3 && (
                    <div className={styles.saved}>
                      <Check size={16} /> Answer saved · Re-checking this gap
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <footer className={styles.caption}>
        <span>0{phase + 1} / 04</span>
        <h3>{titles[phase]}</h3>
        <p>{descriptions[phase]}</p>
      </footer>
    </div>
  );
}
